import React, { useCallback, useMemo, useState } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Drawer,
    CssBaseline,
    useMediaQuery,
    useTheme,
    Dialog,
    Container,
    Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MenuIcon from '@mui/icons-material/Menu';
import { useTitle } from '@dyl/systems/TitleManager';
import useStyles from './styles';
import NavProfile from './NavProfile';
import NavLinks from './NavLinks';
import NavLogout from './NavLogout';
import SectionDividers from '../SectionDividers';
import withProps from '@dyl/utils/withProps';
import { Close as CloseIcon, Settings as SettingsIcon } from '@mui/icons-material';
import useEffectPlus from '@dyl/hooks/useEffectPlus';
import { useSafeArea } from '@dyl/systems/SafeAreaManager';
import useMemoObject from '@dyl/hooks/useMemoObject';
import { SettingsModal, useSettings } from '@dyl/systems/SettingsManager';

/**
 * @param {Object} props
 * @param {{ to: string, title: string }[]} [props.navLinks]
 * @param {JSX.Element} props.children
 */
function MainAppTemplate({
    navLinks,
    children,
    ...other
}) {
    const title = useTitle();
    const theme = useTheme();

    const shouldUseDrawer = useMediaQuery(theme.breakpoints.up('sm'));
    const isDrawerPersistent  = useMediaQuery(theme.breakpoints.up('md'));

    const { shouldOpenSidebarAtStartup, shouldAutoHideSidebarOnScreenShrink } = useSettings();

    // Starts open if view wide enough (cannot use media query in initial value)
    const [drawerOpen, setDrawerOpen] = useState(
        shouldOpenSidebarAtStartup === 'always'
        || (
            shouldOpenSidebarAtStartup === 'only-larger-screens'
            && window.innerWidth >= theme.breakpoints.values.md
        )
    );

    const openDrawer = useCallback(() => setDrawerOpen(true), []);
    const closeDrawer = useCallback(() => setDrawerOpen(false), []);

    const [isSettingsOpen, setSettingsOpen] = useState(false);

    const classes = useStyles({ drawerOpen });

    const MenuComponent = useMemo(() => (
        shouldUseDrawer ? withProps(Drawer, {
            variant: isDrawerPersistent ? 'persistent' : 'temporary',
            anchor: 'left',
            classes: {
                root: classes.drawer,
                paper: classes.drawerPaper,
            },
            onClose: closeDrawer,
        })
        : withProps(Dialog, {
            fullScreen: true,
        })
    ), [shouldUseDrawer, isDrawerPersistent]);

    useEffectPlus(({ isFirstMount }) => {
        if (!isFirstMount && !isDrawerPersistent && shouldAutoHideSidebarOnScreenShrink) {
            closeDrawer();
        }
    }, [isDrawerPersistent]);

    return (
        <div className={window.location.pathname.includes('welcome')
            ? classes.welcomeRoot
            : classes.root} {...other}>
            <CssBaseline/>

            <MenuComponent key='menu' open={drawerOpen}>
                <Toolbar className={classes.drawerToolbar} disableGutters>
                    <IconButton
                        color='inherit'
                        onClick={closeDrawer}
                        className={classes.drawerBackButton}
                        size="large">
                        {shouldUseDrawer ? <ArrowBackIcon/> : <CloseIcon />}
                    </IconButton>
                </Toolbar>

                <SectionDividers noDefaultStyles>
                    <NavProfile />
                    <NavLinks
                        links={navLinks}
                        onLinkClick={() => !isDrawerPersistent && closeDrawer()}
                    />
                    <NavLogout />
                </SectionDividers>
            </MenuComponent>

            <AppBar
                data-testid='app-top-bar'
                position='fixed'
                color='primary'
                className={classes.appBar}
            >
                <Toolbar disableGutters={!drawerOpen || !isDrawerPersistent}>
                    {(!drawerOpen || !isDrawerPersistent) && (
                        <IconButton
                            data-testid='open-nav-bar'
                            color='inherit'
                            onClick={openDrawer}
                            size="large">
                            <MenuIcon/>
                        </IconButton>
                    )}
                    <Typography variant='h6' noWrap className={classes.appBarTitle}>
                        {title}
                    </Typography>

                    <Tooltip title='Preferences'>
                        <IconButton onClick={() => setSettingsOpen(true)} color='inherit' size="large">
                            <SettingsIcon />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <div className={classes.contentBox}>
                <Container maxWidth='lg' component='main'
                    className={classes.content}
                >
                    <div className={classes.innerSpacer} />
                    {children}
                    <SafeAreaBottomSpacer />
                </Container>
            </div>

            <SettingsModal
                open={isSettingsOpen}
                onClose={() => setSettingsOpen(false)}
            />
        </div>
    );
};

function SafeAreaBottomSpacer() {
    const { insets } = useSafeArea();

    const style = useMemoObject({
        minHeight: insets.bottom,
    });

    return <div style={style} />;
}

export default MainAppTemplate;